import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganizationMembers } from '@/hooks/api/useOrganizations';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorMessage } from '@/components/ui/error-message';

const validationSchema = Yup.object().shape({
  userId: Yup.string().required('Please select a user'),
  role: Yup.string().required('Role is required').default('CAN_EDIT'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  userId: '',
  role: 'CAN_EDIT',
};

interface AddWorkspaceMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (userId: string, role: string) => void;
  loading?: boolean;
  organizationId: string;
  existingMemberIds?: string[];
}

export function AddWorkspaceMemberDialog({
  open,
  onOpenChange,
  onAdd,
  loading = false,
  organizationId,
  existingMemberIds = [],
}: AddWorkspaceMemberDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const selectedUserId = watch('userId');
  const role = watch('role');

  // Fetch organization members to select from
  const { data: membersData, isLoading: membersLoading } = useOrganizationMembers(
    organizationId,
    { page: 1, limit: 100 }
  );

  // Filter members by search term and exclude existing workspace members
  const filteredMembers = membersData?.data?.filter((member) => {
    if (existingMemberIds.includes(member._id)) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      member.email.toLowerCase().includes(term) ||
      (member.name?.toLowerCase().includes(term)) ||
      (member.last_name?.toLowerCase().includes(term))
    );
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setSearchTerm('');
    }
  }, [open, reset]);

  const onFormSubmit = (data: FormData) => {
    onAdd(data.userId, data.role);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultValues);
      setSearchTerm('');
    }
    onOpenChange(newOpen);
  };

  const selectedUser = membersData?.data?.find((m) => m._id === selectedUserId);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Add Member to Workspace</DialogTitle>
            <DialogDescription>
              Select an organization member to add to this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Search Members</label>
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Select Member</label>
              {membersLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : !filteredMembers || filteredMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  {searchTerm ? 'No members found matching your search' : 'No members available'}
                </p>
              ) : (
                <div className="max-h-[200px] overflow-y-auto border rounded-md">
                  {filteredMembers.map((member) => (
                    <div
                      key={member._id}
                      className={`p-3 cursor-pointer hover:bg-muted border-b last:border-b-0 ${
                        selectedUserId === member._id ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800' : ''
                      }`}
                      onClick={() => setValue('userId', member._id)}
                    >
                      <div className="font-medium">
                        {[member.name, member.last_name].filter(Boolean).join(' ') || 'No name'}
                      </div>
                      <div className="text-sm text-muted-foreground">{member.email}</div>
                      <div className="text-xs text-muted-foreground capitalize">
                        Org role: {member.role.toLowerCase()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <ErrorMessage message={errors.userId?.message} />
              {selectedUser && (
                <p className="text-sm text-green-600">
                  Selected: {[selectedUser.name, selectedUser.last_name].filter(Boolean).join(' ') || selectedUser.email}
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <label htmlFor="role" className="text-sm font-medium">
                Workspace Role
              </label>
              <Select value={role} onValueChange={(val) => setValue('role', val)} disabled={loading}>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CAN_EDIT">Can Edit</SelectItem>
                  <SelectItem value="CAN_VIEW">Can View</SelectItem>
                </SelectContent>
              </Select>
              <ErrorMessage message={errors.role?.message} />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedUserId}>
              {loading ? 'Adding...' : 'Add Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
